/**
 * Mock per-site dashboards. The WASCI fixture mirrors the screenshots
 * provided by the user (Hilton Washington DC, MX450 + 26 switches + 260 APs).
 *
 * A second fixture (PARIS) covers the smaller Marriott site so the picker
 * has more than one option.
 */
import type {
  ConnectedClient,
  SiteDashboard,
  SiteDevice,
  ThroughputPoint,
} from "@/api/schemas/sites";

const NOW = Date.now();
const MIN = 60_000;
const HOUR = 60 * MIN;
function isoAgo(ms: number) { return new Date(NOW - ms).toISOString(); }

/* ============================================================
 * Helpers — synthesize realistic-looking data without bloating
 * the bundle with thousands of literal rows.
 * ============================================================ */

/** Build a 24h throughput series with a sinusoidal daily pattern + noise. */
function buildThroughput(peakBps: number): ThroughputPoint[] {
  // 24 hourly bins, peak around 16:00 site-local.
  return Array.from({ length: 24 }, (_, i) => {
    const t = isoAgo((23 - i) * HOUR);
    const angle = ((i + 8) / 24) * 2 * Math.PI; // shift so peak ~hour 16
    const wave = (1 + Math.sin(angle - Math.PI / 2)) / 2;
    const rx = peakBps * (0.4 + 0.6 * wave) * (1 + (Math.random() - 0.5) * 0.25);
    const tx = rx * (0.45 + Math.random() * 0.15);
    return { t, rxBps: Math.max(0, Math.round(rx)), txBps: Math.max(0, Math.round(tx)) };
  });
}

function buildClientBandwidthUsage(peakBps: number): ThroughputPoint[] {
  return Array.from({ length: 24 }, (_, i) => {
    const t = isoAgo((23 - i) * HOUR);
    const angle = ((i + 5) / 24) * 2 * Math.PI;
    const wave = (1 + Math.sin(angle - Math.PI / 2)) / 2;
    const downstream = peakBps * 0.65 * (0.35 + 0.65 * wave);
    const upstream = downstream * (0.18 + (i % 4) * 0.03);
    return {
      t,
      rxBps: Math.max(0, Math.round(downstream)),
      txBps: Math.max(0, Math.round(upstream)),
    };
  });
}

/** Build N synthesized switch entries for a site. */
function buildSwitches(prefix: string, count: number, modelMix: string[]): SiteDevice[] {
  return Array.from({ length: count }, (_, i) => {
    const model = modelMix[i % modelMix.length]!;
    const dormant = i >= count - 2; // last 2 are dormant spares
    return {
      serial: `Q2${"PWHX"[i % 4]!}-${(1000 + i).toString(36).toUpperCase().slice(0, 4)}-${(7000 + i).toString(36).toUpperCase().slice(0, 4)}`,
      name: dormant ? `${prefix.split("-").slice(0, 2).join("-")}_GTK_SPARE` : `${prefix}-${i + 1}`,
      model,
      tags: [],
      status: dormant ? "dormant" : "online",
      lanIp: dormant ? null : `192.168.0.${100 + i}`,
      publicIp: dormant ? null : "65.213.241.2",
      lastSeen: dormant ? null : isoAgo(((i % 5) + 1) * MIN),
      deviceClass: "switch",
    };
  });
}

/** Build N synthesized AP entries for a site. */
function buildAps(prefix: string, count: number, modelMix: string[]): SiteDevice[] {
  return Array.from({ length: count }, (_, i) => {
    const model = modelMix[i % modelMix.length]!;
    return {
      serial: `Q2KD-${(2000 + i).toString(36).toUpperCase().slice(0, 4)}-${(8000 + i).toString(36).toUpperCase().slice(0, 4)}`,
      name: `${prefix}-${i + 1}-${(i % 12) + 1 < 10 ? "0" : ""}${(i % 12) + 1}`,
      model,
      tags: [],
      status: "online",
      lanIp: `192.168.${i < 60 ? 0 : i < 130 ? 1 : i < 200 ? 2 : 3}.${20 + (i % 220)}`,
      publicIp: "65.213.241.2",
      lastSeen: isoAgo(((i % 7) + 1) * MIN),
      deviceClass: "wireless",
    };
  });
}

/** Build N connected clients with varied bands of usage. */
function buildClients(count: number): ConnectedClient[] {
  const SSIDS = ["Hilton Honors", "WASCIHOH", "Hilton Honors Lobby", "Wired"];
  const VLANS = ["520", "14", "528", "Port 32"];
  const VENDORS = ["Apple", "Intel", "Broadcom", "Samsung", "Dell", "—"];
  const OSES = ["iOS18.7.7", "Windows 10", "Android", "macOS 14", "—"];
  const NAME_PREFIXES = ["1JR6QUAL", "Carlos-Sedas", "OVI", "wifi-cust"];
  return Array.from({ length: count }, (_, i) => {
    const ssidIx = i % SSIDS.length;
    const isWired = ssidIx === 3;
    const heavy = i < 8; // first ~8 are heavy users
    const macParts = Array.from({ length: 6 }, (_, j) => ((i * 17 + j * 31) % 256).toString(16).padStart(2, "0"));
    return {
      id: `cl-${i}`,
      name: i < 5
        ? (i === 0 ? "OVI ETH0" : i === 4 ? "Carlos-Sedas-iPhone" : `${NAME_PREFIXES[i % NAME_PREFIXES.length]}_LT${i}${i}`)
        : `${(i * 13 + 100).toString(16)}-${(i * 7 + 200).toString(16)}-${(i * 11 + 300).toString(16)}-${(i * 17 + 400).toString(16)}`,
      mac: macParts.join(":"),
      manufacturer: VENDORS[i % VENDORS.length] ?? null,
      os: OSES[i % OSES.length] ?? null,
      ip: isWired ? `172.19.${(i % 5) + 30}.${i + 1}` : `192.168.${20 + (i % 6)}.${(i * 3 + 5) % 250}`,
      ssid: SSIDS[ssidIx] ?? null,
      vlanOrPort: VLANS[ssidIx] ?? null,
      lastSeen: isoAgo((i % 6) * 30 * 1000),
      online: true,
      bytesDown: heavy ? 200_000_000 + i * 30_000_000 : 5_000_000 + i * 200_000,
      bytesUp: heavy ? 10_000_000 + i * 3_000_000 : 500_000 + i * 20_000,
    };
  });
}

function buildFirmwareUpgrades(networkId: string, networkName: string) {
  return [
    {
      upgradeId: `${networkId}-fw-1`,
      upgradeBatchId: `${networkId}-batch-1`,
      networkId,
      networkName,
      status: "Scheduled",
      phase: "upcoming" as const,
      scheduledAt: isoAgo(-2 * HOUR),
      completedAt: null,
      productTypes: ["wireless"],
      fromVersion: "MR 30.6",
      toVersion: "MR 31.1",
      releaseType: "stable",
      timezone: "America/New_York",
    },
    {
      upgradeId: `${networkId}-fw-2`,
      upgradeBatchId: `${networkId}-batch-2`,
      networkId,
      networkName,
      status: "Scheduled",
      phase: "upcoming" as const,
      scheduledAt: isoAgo(-4 * HOUR),
      completedAt: null,
      productTypes: ["switch"],
      fromVersion: "MS 15.19",
      toVersion: "MS 16.4",
      releaseType: "stable",
      timezone: "America/New_York",
    },
    {
      upgradeId: `${networkId}-fw-3`,
      upgradeBatchId: `${networkId}-batch-3`,
      networkId,
      networkName,
      status: "Completed",
      phase: "completed" as const,
      scheduledAt: isoAgo(3 * 24 * HOUR),
      completedAt: isoAgo(3 * 24 * HOUR - 45 * MIN),
      productTypes: ["appliance"],
      fromVersion: "MX 18.107",
      toVersion: "MX 18.211",
      releaseType: "stable",
      timezone: "America/New_York",
    },
  ];
}

function buildAlertsHistory(networkId: string) {
  return [
    {
      occurredAt: isoAgo(20 * MIN),
      alertTypeId: "gateway_down",
      alertType: "Gateway down",
      deviceSerial: `${networkId}-MX1`,
      emailSentAt: isoAgo(19 * MIN),
      pushSentAt: isoAgo(19 * MIN),
      smsSentAt: null,
      webhookSentAt: isoAgo(19 * MIN),
    },
    {
      occurredAt: isoAgo(95 * MIN),
      alertTypeId: "settings_changed",
      alertType: "Settings changed",
      deviceSerial: `${networkId}-SW1`,
      emailSentAt: isoAgo(94 * MIN),
      pushSentAt: null,
      smsSentAt: null,
      webhookSentAt: isoAgo(94 * MIN),
    },
    {
      occurredAt: isoAgo(30 * HOUR),
      alertTypeId: "vpn_connectivity",
      alertType: "VPN connectivity changed",
      deviceSerial: `${networkId}-MX2`,
      emailSentAt: isoAgo(30 * HOUR - 2 * MIN),
      pushSentAt: isoAgo(30 * HOUR - 2 * MIN),
      smsSentAt: null,
      webhookSentAt: null,
    },
  ];
}

function buildAssuranceAlerts(networkId: string, networkName: string) {
  return [
    {
      id: `${networkId}-assurance-1`,
      category: "connectivity",
      type: "crc_errors_error",
      title: "CRC errors detected",
      description: "Very high proportion of CRC errors on a switch port.",
      severity: "warning",
      startedAt: isoAgo(15 * MIN),
      resolvedAt: null,
      dismissedAt: null,
      networkId,
      networkName,
      deviceSerial: `${networkId}-SW1`,
      deviceName: `${networkName} core switch`,
      deviceType: "switch",
    },
    {
      id: `${networkId}-assurance-2`,
      category: "device_health",
      type: "gateway_unreachable",
      title: "Gateway unreachable",
      description: "Appliance heartbeat has intermittent loss.",
      severity: "critical",
      startedAt: isoAgo(65 * MIN),
      resolvedAt: null,
      dismissedAt: null,
      networkId,
      networkName,
      deviceSerial: `${networkId}-MX1`,
      deviceName: `${networkName} edge MX`,
      deviceType: "appliance",
    },
  ];
}

function buildWirelessFailedConnections(networkId: string) {
  return [
    {
      ssidNumber: 0,
      vlan: 0,
      clientMac: "72:55:bc:6f:c8:6a",
      serial: `${networkId}-AP1`,
      radio: 0,
      failureStep: "dhcp",
      type: "NO DHCP ack",
      ts: isoAgo(6 * MIN),
    },
    {
      ssidNumber: 0,
      vlan: 0,
      clientMac: "c6:74:87:bd:a7:f2",
      serial: `${networkId}-AP2`,
      radio: 1,
      failureStep: "auth",
      type: "802.1X auth fail",
      ts: isoAgo(11 * MIN),
    },
    {
      ssidNumber: 6,
      vlan: 30,
      clientMac: "f2:85:13:a3:ae:e2",
      serial: `${networkId}-AP3`,
      radio: 1,
      failureStep: "dns",
      type: "DNS lookup timeout",
      ts: isoAgo(19 * MIN),
    },
  ];
}

function buildLicenses(networkId: string) {
  return [
    {
      id: `${networkId}-lic-1`,
      licenseType: "Enterprise",
      licenseKey: "LIC-ENT-4451",
      deviceSerial: `${networkId}-MX1`,
      networkId,
      state: "active",
      expirationDate: isoAgo(-90 * 24 * HOUR),
      seatCount: 1,
    },
    {
      id: `${networkId}-lic-2`,
      licenseType: "MR Advanced",
      licenseKey: "LIC-MR-8820",
      deviceSerial: `${networkId}-AP1`,
      networkId,
      state: "expiring",
      expirationDate: isoAgo(-14 * 24 * HOUR),
      seatCount: 25,
    },
  ];
}

/* ============================================================
 * WASCI — Hilton Washington DC (matches the screenshots)
 * ============================================================ */

const WASCI_SWITCHES = buildSwitches("HLT-WASCI-3", 26, ["MS350-24P", "MS350-48", "MS350-48FP", "MS350-24"]);
const WASCI_APPLIANCE: SiteDevice = {
  serial: "Q2DW-EA5S-VF94",
  name: "HLT-WASCI-425-MDF-CS01",
  model: "MX450",
  tags: [],
  status: "online",
  lanIp: "192.168.0.101",
  publicIp: "65.213.241.2",
  lastSeen: isoAgo(20 * 1000),
  deviceClass: "appliance",
};
const WASCI_APS = buildAps("HLT-WASCI-3", 28, ["MR30H", "MR42"]); // sample 28 of 260 for the table
const WASCI_DEVICES: SiteDevice[] = [WASCI_APPLIANCE, ...WASCI_SWITCHES, ...WASCI_APS];

const WASCI: SiteDashboard = {
  summary: {
    slug: "wasci",
    networkId: "L_1738",
    code: "NA-USA-DC-GIA-WASCI",
    isTest: false,
    rxLimitKbps: 0,
    txLimitKbps: 0,
    friendlyName: "HLT-UnoNet-PROD-NA-USA-DC",
    org: "Hilton · Washington DC",
    vendor: "meraki",
    externalDashboardUrl:
      "https://dashboard.meraki.com/o/123456/n/L_1738/manage/nodes/new_list",
    activeClients: 1287,
    heavyClientsLastHour: 13,
    peakBps: 4270, // 4.27 Kb/s as in the screenshot
    uplinks: { total: 2, active: 2 },
  },
  rollups: [
    { label: "Appliances", total: 1, byStatus: { online: 1 } },
    { label: "Switches", total: 26, byStatus: { online: 24, dormant: 2 } },
    { label: "Wireless", total: 260, byStatus: { online: 260 } },
  ],
  wanUplinks: [
    {
      label: "WAN1", model: "MX450", state: "active",
      publicIp: "65.213.241.2", lanIp: "65.213.241.2", gateway: "65.213.241.1",
      primaryDns: "8.8.8.8", secondaryDns: "198.6.1.60"
    },
    {
      label: "WAN2", model: "MX450", state: "ready",
      publicIp: "72.108.177.187", lanIp: "72.108.177.187", gateway: "72.108.177.1",
      primaryDns: "198.224.190.135", secondaryDns: "198.224.191.135"
    },
  ],
  throughput: buildThroughput(4270),
  clientBandwidthUsage: buildClientBandwidthUsage(12_400_000),
  firmwareUpgrades: buildFirmwareUpgrades("L_1738", "Minions"),
  alertsHistory: buildAlertsHistory("L_1738"),
  licenses: buildLicenses("L_1738"),
  assuranceAlerts: buildAssuranceAlerts("L_1738", "Minions"),
  wirelessFailedConnections: buildWirelessFailedConnections("L_1738"),
  devices: WASCI_DEVICES,
  clients: buildClients(28),
};

/* ============================================================
 * NYC21 — smaller fixture so the picker has options
 * ============================================================ */

const NYC21: SiteDashboard = {
  summary: {
    slug: "nyc21",
    networkId: "L_9121",
    code: "NA-USA-NY-MAR-NYC21",
    isTest: false,
    rxLimitKbps: 0,
    txLimitKbps: 0,
    friendlyName: "Marriott Times Sq · NYC-21",
    org: "Marriott · New York",
    vendor: "meraki",
    externalDashboardUrl:
      "https://dashboard.meraki.com/o/123456/n/L_9121/manage/nodes/new_list",
    activeClients: 412,
    heavyClientsLastHour: 4,
    peakBps: 2_400_000,
    uplinks: { total: 2, active: 2 },
  },
  rollups: [
    { label: "Appliances", total: 1, byStatus: { online: 1 } },
    { label: "Switches", total: 8, byStatus: { online: 7, alerting: 1 } },
    { label: "Wireless", total: 5, byStatus: { online: 5 } },
  ],
  wanUplinks: [
    {
      label: "WAN1", model: "MX84", state: "active",
      publicIp: "68.97.4.118", lanIp: "10.21.0.1", gateway: "10.21.0.254",
      primaryDns: "8.8.8.8", secondaryDns: "1.1.1.1"
    },
    {
      label: "WAN2", model: "MX84", state: "ready",
      publicIp: "203.0.113.18", lanIp: "10.21.0.2", gateway: "10.21.0.254",
      primaryDns: "8.8.8.8", secondaryDns: "1.1.1.1"
    },
  ],
  throughput: buildThroughput(2_400_000),
  clientBandwidthUsage: buildClientBandwidthUsage(6_800_000),
  firmwareUpgrades: buildFirmwareUpgrades("L_9121", "NYC-21"),
  alertsHistory: buildAlertsHistory("L_9121"),
  licenses: buildLicenses("L_9121"),
  assuranceAlerts: buildAssuranceAlerts("L_9121", "NYC-21"),
  wirelessFailedConnections: buildWirelessFailedConnections("L_9121"),
  devices: [
    { serial: "Q2KN-9X4P-AAAA", name: "MX84-edge-01", model: "MX84", tags: [], status: "online", lanIp: "10.21.0.1", publicIp: "68.97.4.118", lastSeen: isoAgo(10_000), deviceClass: "appliance" },
    { serial: "Q2HW-K3LM-NNNN", name: "MS425-core-08", model: "MS425", tags: [], status: "alerting", lanIp: "10.21.0.8", publicIp: "68.97.4.118", lastSeen: isoAgo(2 * MIN), deviceClass: "switch" },
    { serial: "Q2HW-K3LM-9999", name: "MS425-core-09", model: "MS425", tags: [], status: "online", lanIp: "10.21.0.9", publicIp: "68.97.4.118", lastSeen: isoAgo(15_000), deviceClass: "switch" },
    ...buildAps("HLT-NYC21-3", 5, ["MR55", "MR42"]),
  ],
  clients: buildClients(15),
};

/* ============================================================ */

export const MOCK_SITE_DASHBOARDS: Record<string, SiteDashboard> = {
  wasci: WASCI,
  nyc21: NYC21,
};

/** All known sites — drives the picker + the /sites list page. */
export const MOCK_SITE_SUMMARIES = Object.values(MOCK_SITE_DASHBOARDS).map((d) => d.summary);
